<template>
  <div class="projects-container">
    <!-- Toolbar -->
    <table-toolbar
      v-if="showToolbar"
      :show-title="showTitle"
      :title="title"
      :show-count="true"
      :current-count="filteredProjects.length"
      :total-count="totalProjects"
      :searchable="showSearch"
      :search-query="searchQuery"
      @search-change="searchQuery = $event"
      :show-per-page-selector="showDisplayCount"
      :per-page="displayCount"
      @per-page-change="displayCount = $event"
      :show-refresh="false"
      :show-view-toggle="showViewToggle"
      :view-mode="viewMode"
      @view-mode-change="viewMode = $event"
      :show-sort-filter="showSortFilter"
      :sort-options="sortOptions"
      :sort-by="sortColumn"
      :sort-direction="sortDirection"
      @sort-change="sortColumn = $event"
      @sort-direction-change="sortDirection = $event"
      :filter-options="filterOptionsConfig"
      @filter-change="handleFilterChange"
      @clear-filters="clearFilters"
      :disabled="loading"
    >
      <!-- Action Buttons Slot -->
      <template #action-buttons>
        <!-- New Project Button -->
        <themed-button 
          v-if="showCreateNew && canCreateProject"
          @click="addProject"
          variant="default"
          size="default"
          aria-label="Create new project"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>New Project</span>
        </themed-button>
      </template>
    </table-toolbar>
    
    <!-- Grid View -->
    <themed-grid
      v-if="viewMode === 'grid'"
      :data="filteredProjects"
      :fields="gridFields"
      :columns="4"
      :pagination="paginationConfig"
      :clickable="true"
      :loading="loading"
      empty-message="No projects found"
      empty-icon="project-icon"
      item-key="id"
      class="projects-themed-grid"
      @card-click="handleCardClick"
      @page-change="handlePageChange"
    />
    
    <!-- List View -->
    <themed-table
      v-else
      ref="projectsTable"
      :data="filteredProjects"
      :columns="tableColumns"
      :pagination="paginationConfig"
      :hoverable="true"
      aria-label="Projects Table"
      @row-click="handleRowClick"
      @page-change="handlePageChange"
    />

    <!-- New Project Modal -->
    <new-project-modal />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemeIcon from '../ThemeIcon.vue';
import NewProjectModal from './NewProjectModal.vue';
import TableToolbar from '../table/TableToolbar.vue';
import { formatNumber, formatShorthandCurrency, formatCompactNumber } from '../../../../utils/formatters';

export default {
  name: 'ThemedProjects',
  
  components: {
    ThemedButton,
    ThemedTable,
    ThemedGrid,
    ThemeIcon,
    NewProjectModal,
    TableToolbar
  },

  props: {
    projectData: {
      type: Array,
      required: true
    },
    
    // Toolbar configuration
    showToolbar: {
      type: Boolean,
      default: true
    },
    
    showHeader: {
      type: Boolean,
      default: true
    },
    
    smallHeader: {
      type: Boolean,
      default: false
    },
    
    showTitle: {
      type: Boolean,
      default: true
    },
    
    showSearch: {
      type: Boolean,
      default: true
    },
    
    showViewToggle: {
      type: Boolean,
      default: true
    },
    
    showSortFilter: {
      type: Boolean,
      default: true
    },
    
    showFilters: {
      type: Boolean,
      default: true
    },
    
    showDisplayCount: {
      type: Boolean,
      default: true
    },
    
    showCreateNew: {
      type: Boolean,
      default: false
    },
    
    showNewProjectButton: {
      type: Boolean,
      default: false
    },
    
    showCompletedProjects: {
      type: Boolean,
      default: false
    },
    
    centerCards: {
      type: Boolean,
      default: false
    },
    
    // View configuration
    view: {
      type: String,
      default: 'grid',
      validator: (value) => ['grid', 'table'].includes(value)
    },
    
    defaultViewMode: {
      type: String,
      default: 'grid', // 'grid' or 'list'
      validator: (value) => ['grid', 'list'].includes(value)
    },
    
    // Pagination configuration
    perPage: {
      type: Number,
      default: 10
    },
    
    defaultPageSize: {
      type: Number,
      default: 10,
      validator: (value) => [5, 10, 20, 50, 100].includes(value)
    },
    
    showPagination: {
      type: Boolean,
      default: true
    },
    
    pageSizeOptions: {
      type: Array,
      default: () => [5, 10, 20]
    },
    
    // Sort configuration
    defaultSortBy: {
      type: String,
      default: 'name'
    },
    
    defaultSortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    
    // Custom title
    title: {
      type: String,
      default: 'Projects'
    },
    
    // Disable localStorage persistence
    disablePreferences: {
      type: Boolean,
      default: false
    },
    
    // Custom columns for table view
    columns: {
      type: Array,
      default: null
    }
  },

  data() {
    return {
      viewMode: this.defaultViewMode,
      searchQuery: '',
      sortColumn: '',
      sortDirection: this.defaultSortDirection,
      showCompletedProjectsValue: this.showCompletedProjects,
      displayCount: this.defaultPageSize || this.perPage,
      currentPage: 1,
      loading: false,
      allProjects: [],
      sortOptions: [
        { value: 'name', label: 'Name' },
        { value: 'project_type', label: 'Project Type' },
        { value: 'priority', label: 'Priority' },
        { value: 'status', label: 'Status' },
        { value: 'stage', label: 'Stage' },
        { value: 'property_type', label: 'Property Type' },
        { value: 'property_usage', label: 'Property Usage' },
        { value: 'property_region', label: 'Property Region' }
      ]
    };
  },

  computed: {
    ...mapState(['projects']),

    canCreateProject() {
      return this.$gate.can('create', 'project');
    },

    totalProjects() {
      return this.allProjects.length;
    },

    filterOptionsConfig() {
      const filters = [];
      
      if (this.showFilters) {
        filters.push({
          type: 'toggle',
          label: 'Show Completed Projects',
          value: this.showCompletedProjectsValue,
          model: 'showCompletedProjects'
        });
      }
      
      return filters;
    },

    gridFields() {
      return [
        // Title
        {
          field: 'name',
          type: 'title'
        },
        // Status Badge (top-right)
        {
          field: 'status',
          type: 'badge',
          position: 'topRight',
          transform: (value) => value || 'ACTIVE',
          class: 'badge--blue'
        },
        // Project Type
        {
          field: 'project_type',
          label: 'Project Type:',
          type: 'text',
          class: 'project-metric-row'
        },
        // Stage
        {
          field: 'stage',
          label: 'Stage:',
          type: 'text',
          class: 'project-metric-row'
        },
        // Current Sq. Ft
        {
          field: 'current_sq_ft',
          label: 'Current Sq. Ft:',
          type: 'text',
          transform: (value) => formatCompactNumber(value),
          class: 'project-metric-row project-metric-row--alt'
        },
        // Required Sq. Ft
        {
          field: 'required_sq_ft',
          label: 'Required Sq. Ft:',
          type: 'text',
          transform: (value) => formatCompactNumber(value),
          class: 'project-metric-row project-metric-row--alt'
        },
        // Property Type
        {
          field: 'property_type',
          label: 'Property Type:',
          type: 'text',
          class: 'project-metric-row'
        },
        // Time Remaining
        {
          field: 'time_remaining',
          label: 'Time Remaining:',
          type: 'text',
          // Was `value || '1.6 Years'`, which printed the same invented figure on
          // every project whose time_remaining was empty - including New Location
          // projects, where time remaining on an existing lease has no meaning.
          transform: (value) => value || '—',
          class: 'project-metric-row'
        },
        // Count badges row (Sites and Comps) - 2 columns with circular badges
        {
          field: 'count_badges',
          columns: 2,
          class: 'project-count-badges-row',
          fields: [
            {
              field: 'sites_count',
              label: 'SITES',
              type: 'number',
              badge: true,
              badgeClass: 'project-count-badge project-count-badge--sites',
              labelInValue: true,  // Show label inside the badge
              showLabel: true
            },
            {
              field: 'comps_count',
              label: 'COMPS',
              type: 'number',
              badge: true,
              badgeClass: 'project-count-badge project-count-badge--comps',
              labelInValue: true,  // Show label inside the badge
              showLabel: true
            }
          ]
        },
        // Footer badges row (Priority and Benchmark) - 2 columns
        {
          field: 'footer_badges',
          columns: 2,
          class: 'project-footer-badges-row',
          fields: [
            {
              field: 'priority',
              label: 'PRIORITY',
              type: 'text',
              transform: (value) => value || 'MEDIUM',
              badge: true,
              // Was the fixed string 'badge--yellow', so Low, Medium, High and
              // Urgent all rendered as the same amber pill and the badge carried
              // no information.
              badgeClass: (value) => {
                const priority = String(value || 'MEDIUM').toLowerCase();
                const colors = {
                  urgent: 'badge--red',
                  high: 'badge--orange',
                  medium: 'badge--yellow',
                  low: 'badge--gray',
                };

                return `${colors[priority] || 'badge--yellow'} project-footer-badge`;
              },
              showLabel: false,  // Don't show label for footer badges
              infoIcon: 'projects-priority'
            },
            {
              field: 'benchmark',
              label: 'MARKET',
              type: 'text',
              // Was `item.benchmark || 46`, which invented a 46% figure whenever
              // benchmark was absent - and, because || treats 0 as falsy, also
              // overwrote a genuine at-market reading of 0 with 46.
              transform: (value, item) => {
                const benchmark = item.benchmark;
                if (benchmark === null || benchmark === undefined || benchmark === '') {
                  return '—';
                }

                if (Number(benchmark) === 0) {
                  // label: 'MARKET' with labelInValue:true means the rendered
                  // badge text will be "AT MARKET" — returning 'AT MARKET' here
                  // doubled the word to "AT MARKET MARKET".
                  return 'AT';
                }

                return `${Math.abs(benchmark)}% ${benchmark < 0 ? 'UNDER' : 'OVER'}`;
              },
              badge: true,
              badgeClass: (value, item) => {
                const benchmark = item.benchmark;
                let baseClass = 'badge--gray';

                if (benchmark !== null && benchmark !== undefined && benchmark !== '') {
                  if (Number(benchmark) === 0) {
                    // Backend sends benchmark_color 'badge-yellow' for 0.
                    // Use yellow to match, not green.
                    baseClass = 'badge--yellow';
                  } else {
                    baseClass = Number(benchmark) > 0 ? 'badge--red' : 'badge--green';
                  }
                }

                return `${baseClass} project-footer-badge`;
              },
              labelInValue: true,  // Show label inside the badge
              infoIcon: 'projects-under-over-market'
            }
          ]
        }
      ];
    },

    filteredProjects() {
      let filtered = [...this.allProjects];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(project => {
          return (
            project.name?.toLowerCase().includes(query) ||
            project.project_type?.toLowerCase().includes(query) ||
            project.stage?.toLowerCase().includes(query)
          );
        });
      }

      // Apply completed projects filter
      if (!this.showCompletedProjectsValue) {
        filtered = filtered.filter(project => project.status !== 'Completed');
      }

      // Apply sorting
      if (this.sortColumn) {
        filtered.sort((a, b) => {
          const aVal = a[this.sortColumn];
          const bVal = b[this.sortColumn];
          
          if (aVal === bVal) return 0;
          
          const comparison = aVal < bVal ? -1 : 1;
          return this.sortDirection === 'asc' ? comparison : -comparison;
        });
      }

      return filtered;
    },

    paginationConfig() {
      if (!this.showPagination) {
        return false;
      }
      
      const totalPages = Math.ceil(this.filteredProjects.length / this.displayCount);
      
      return {
        current: this.currentPage,
        total: totalPages,
        perPage: this.displayCount,
        totalItems: this.filteredProjects.length
      };
    },

    tableColumns() {
      // Use custom columns if provided
      if (this.columns) {
        return this.columns;
      }
      
      return [
        {
          field: 'name',
          label: 'Name',
          sortable: true,
          searchable: true,
          align: 'left',
          formatter: (value, row) => {
            return `<a href="${row.view_url}" class="projects-container__name-link">${value}</a>`;
          }
        },
        {
          field: 'start_date_formatted',
          label: 'Start Date',
          align: 'center',
          width:'12%',
          sortable: true
        },
        {
          field: 'end_date_formatted',
          label: 'End Date',
          align: 'center',
          width:'12%',
          sortable: true
        },
        {
          field: 'required_sq_ft',
          label: 'Sq. Ft.',
          sortable: true,
          type: 'number',
          align:'right',
          width:'8%',
          formatter: (value) => this.formatCompactNumber(value)
        },
        {
          field: 'shortened_remaining_lease_cost',
          label: 'Remaining',
          sortable: true,
          width: '100px',
          align:'right',
          formatter: (value) => {
            return `<span class="projects-container__remaining">$${value}</span>`;
          }
        },
        {
          field: 'project_type',
          label: 'Type',
          sortable: true,
          width:'11%',
          searchable: true
        },
        {
          field: 'stage',
          label: 'Stage',
          sortable: true,
          width:'17%',
          searchable: true,
          formatter: (value, row) => {
            const badgeClass = this.getStageBadgeClass(value);
            return `<span class="projects-table-content__badge ${badgeClass}">${value}</span>`;
          }
        },
        {
          field: 'benchmark',
          label: 'OM %',
          sortable: true,
          type: 'number',
          width:'5%',
          align:'right',
          formatter: (value) => {
            return `<span class="projects-container__benchmark">${value}%</span>`;
          }
        }
      ];
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    
    showCompletedProjectsValue() {
      this.currentPage = 1;
    },
    
    viewMode(newValue) {
      if (!this.disablePreferences) {
        localStorage.setItem('projects_view_mode', newValue);
      }
    },
    
    displayCount() {
      this.currentPage = 1;
      if (!this.disablePreferences) {
        localStorage.setItem('projects_display_count', this.displayCount);
      }
    }
  },

  mounted() {
    // Initialize projects data
    this.allProjects = this.projectData;
    
    if (this.projects) {
      this.projects.all = this.projectData;
      this.projects.filtered = this.filteredProjects;
    }

    // Load saved preferences
    if (!this.disablePreferences) {
      const savedViewMode = localStorage.getItem('projects_view_mode');
      if (savedViewMode) {
        this.viewMode = savedViewMode;
      }

      const savedDisplayCount = localStorage.getItem('projects_display_count');
      if (savedDisplayCount) {
        this.displayCount = parseInt(savedDisplayCount);
      }
    }

    // Emit filter request for compatibility
    this.$emit('filterRequest');
  },

  methods: {
    handleFilterChange({ filter, value }) {
      if (filter === 'showCompletedProjects') {
        this.showCompletedProjectsValue = value;
      }
      
      this.$emit('filter-change', {
        showCompletedProjects: this.showCompletedProjectsValue
      });
    },

    clearFilters() {
      this.sortColumn = '';
      this.sortDirection = 'asc';
      this.showCompletedProjectsValue = false;
      this.searchQuery = '';
      this.currentPage = 1;
    },

    handlePageChange(event) {
      if (event && event.page) {
        this.currentPage = event.page;
      }
    },

    handleCardClick(project) {
      window.location.href = project.view_url;
    },

    handleRowClick({ row }) {
      window.location.href = row.view_url;
    },

    addProject() {
      this.$modal.show('new-project');
    },

    formatNumber,
    formatShorthandCurrency,
    formatCompactNumber,

    getStageBadgeClass(stage) {
      if (!stage) return 'projects-table-content__badge--blue';
      
      const stageLower = stage.toLowerCase();
      
      if (stageLower.includes('complete') || stageLower.includes('closed')) {
        return 'projects-table-content__badge--green';
      } else if (stageLower.includes('active') || stageLower.includes('in progress')) {
        return 'projects-table-content__badge--blue';
      } else if (stageLower.includes('pending') || stageLower.includes('review')) {
        return 'projects-table-content__badge--yellow';
      } else if (stageLower.includes('cancelled') || stageLower.includes('on hold')) {
        return 'projects-table-content__badge--red';
      } else if (stageLower.includes('planning') || stageLower.includes('draft')) {
        return 'projects-table-content__badge--purple';
      }
      
      return 'projects-table-content__badge--blue';
    }
  }
};
</script>
